import type { AttributeType, Input, VisualElement } from "@judo/model-api";
import { useCallback, useRef, useSyncExternalStore } from "react";
import { useDataStore } from "../contexts";
import { usePageContext } from "../contexts";
import { useVisualPropertyOverrides } from "../contexts";
import type { DataStoreState } from "../store";
import { deepEqual } from "../utils";
import { useValidation } from "./use-validation";

/**
 * Result from useVisualBinding hook.
 */
export interface VisualBindingResult {
	/** Current value from data store */
	value: unknown;
	/** Change handler */
	onChange: (newValue: unknown) => void;
	/** Validation error if any */
	error: string | null;
	/** Whether element is hidden (via hiddenBy or static hidden) */
	hidden: boolean;
	/** Whether field is disabled */
	disabled: boolean;
	/** Whether field is required */
	required: boolean;
	/** Display label */
	label: string;
	/** Whether field is read-only */
	readOnly: boolean;
	/** Whether data is loading */
	isLoading: boolean;
	/** Whether value has changed from original */
	isDirty: boolean;
}

/**
 * Check if element is an Input type.
 */
function isInput(element: VisualElement): element is Input & VisualElement {
	return "attributeType" in element;
}

/**
 * Dedicated hook for visual element to data binding.
 *
 * IMPORTANT: No memoization of values that could corrupt data consistency.
 * Always reads fresh from DataContext via selector subscription.
 *
 * @param element - Visual element to bind
 * @param attributeType - Optional pre-resolved AttributeType
 * @returns VisualBindingResult
 */
export function useVisualBinding(element: VisualElement, attributeType?: AttributeType): VisualBindingResult {
	const store = useDataStore();
	const pageContext = usePageContext();
	const transferId = pageContext.transferId ?? "";

	// Resolve attribute name from element or provided attributeType
	// element.attributeType is now a resolved AttributeType object, so access .name
	const resolvedAttrType = attributeType ?? (isInput(element) ? element.attributeType : undefined);
	const attributeName = resolvedAttrType?.name;

	// Warn in development if an Input element has an unresolved attributeType
	if (import.meta.env?.DEV && isInput(element) && element.attributeType) {
		if (typeof element.attributeType === "string") {
			console.warn(
				`[useVisualBinding] element.attributeType is a string reference "${element.attributeType}" for element "${element.name}". This means model resolution failed. Expected an AttributeType object with 'name' property.`
			);
		} else if (!element.attributeType.name) {
			console.warn(
				`[useVisualBinding] element.attributeType is missing 'name' property for element "${element.name}". Data binding will not work.`
			);
		}
	}

	// Resolve *By attribute names for dynamic behavior
	const hiddenByAttr = element.hiddenBy?.name;
	const enabledByAttr = element.enabledBy?.name;
	const requiredByAttr = element.requiredBy?.name;

	// Cache for selector result to ensure referential stability
	type SelectorResult = {
		value: unknown;
		isDirty: boolean;
		isLoading: boolean;
		hiddenByValue: unknown;
		enabledByValue: unknown;
		requiredByValue: unknown;
	};
	const cacheRef = useRef<SelectorResult>({
		value: undefined,
		isDirty: false,
		isLoading: false,
		hiddenByValue: undefined,
		enabledByValue: undefined,
		requiredByValue: undefined,
	});

	// Create selector for this specific attribute and *By fields
	const selector = useCallback(
		(state: DataStoreState): SelectorResult => {
			const transferState = state.transfers.get(transferId);
			const data = transferState?.data;

			let newResult: SelectorResult;

			if (!attributeName && !hiddenByAttr && !enabledByAttr && !requiredByAttr) {
				newResult = {
					value: undefined,
					isDirty: false,
					isLoading: transferState?.isLoading ?? false,
					hiddenByValue: undefined,
					enabledByValue: undefined,
					requiredByValue: undefined,
				};
			} else if (!data) {
				newResult = {
					value: undefined,
					isDirty: false,
					isLoading: transferState?.isLoading ?? false,
					hiddenByValue: undefined,
					enabledByValue: undefined,
					requiredByValue: undefined,
				};
			} else {
				newResult = {
					value: attributeName ? data[attributeName] : undefined,
					isDirty: transferState.isDirty,
					isLoading: transferState.isLoading,
					hiddenByValue: hiddenByAttr ? data[hiddenByAttr] : undefined,
					enabledByValue: enabledByAttr ? data[enabledByAttr] : undefined,
					requiredByValue: requiredByAttr ? data[requiredByAttr] : undefined,
				};
			}

			// Return cached result if values are equal (referential stability for useSyncExternalStore)
			if (deepEqual(cacheRef.current, newResult)) {
				return cacheRef.current;
			}
			cacheRef.current = newResult;
			return newResult;
		},
		[transferId, attributeName, hiddenByAttr, enabledByAttr, requiredByAttr]
	);

	// Subscribe to data changes for this selector
	const subscribe = useCallback((cb: () => void) => store.subscribe(selector, cb), [store, selector]);

	const getSnapshot = useCallback(() => store.getSnapshot(selector), [store, selector]);

	const { value, isDirty, isLoading, hiddenByValue, enabledByValue, requiredByValue } = useSyncExternalStore(
		subscribe,
		getSnapshot,
		getSnapshot
	);

	// Change handler - no debounce, immediate update
	const onChange = useCallback(
		(newValue: unknown) => {
			if (!attributeName) return;
			store.updateTransferField(transferId, attributeName, newValue);
		},
		[store, transferId, attributeName]
	);

	// Resolve validation state
	const { error } = useValidation(element);

	// Helper to convert value to boolean (handles string "true"/"false" from XML)
	const toBoolean = (val: unknown, defaultValue: boolean): boolean => {
		if (typeof val === "boolean") return val;
		if (val === "true") return true;
		if (val === "false") return false;
		return defaultValue;
	};

	// Get element properties with fallbacks
	let disabled = toBoolean((element as unknown as Record<string, unknown>).disabled, false);
	let required = isInput(element)
		? toBoolean((element as Input).required, false) || toBoolean(resolvedAttrType?.isRequired, false)
		: false;
	const label = ((element as unknown as Record<string, unknown>).label as string) ?? resolvedAttrType?.name ?? "";
	const elementReadOnly = (element as unknown as Record<string, unknown>).readOnly;
	const attrReadOnly = resolvedAttrType?.isReadOnly;
	const readOnly = toBoolean(elementReadOnly ?? attrReadOnly, false);

	// Apply *By dynamic overrides (data-driven, between static and customization overrides)
	// hiddenBy: if referenced boolean field is true → element is hidden
	let hidden = toBoolean((element as unknown as Record<string, unknown>).hidden, false);
	if (hiddenByAttr && toBoolean(hiddenByValue, false)) {
		hidden = true;
	}
	// enabledBy: when present, fully controls disabled state
	// referenced boolean true → enabled (disabled=false), false → disabled (disabled=true)
	if (enabledByAttr) {
		disabled = !toBoolean(enabledByValue, false);
	}
	// requiredBy: if referenced boolean field is true → element is required
	if (requiredByAttr && toBoolean(requiredByValue, false)) {
		required = true;
	}

	// Visual property overrides (from CustomizationsProvider) - highest priority
	const propertyOverrides = useVisualPropertyOverrides(element);

	return {
		value,
		onChange,
		error,
		hidden: propertyOverrides?.hidden ?? hidden,
		disabled: propertyOverrides?.disabled ?? disabled,
		required: propertyOverrides?.required ?? required,
		label: propertyOverrides?.label ?? label,
		readOnly: propertyOverrides?.readOnly ?? readOnly,
		isLoading,
		isDirty,
	};
}
